import React, { useEffect, useState, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useChat } from './hooks/useChat';
import { useCircles } from './hooks/useCircles';
import { useCircleChat } from './hooks/useCircleChat';
import { usePWAInstall } from './hooks/usePWAInstall';
import { useNotifications } from './hooks/useNotifications';
import { useNavigation, VIEWS } from './hooks/useNavigation';
import { useOnboardingFlow } from './hooks/useOnboardingFlow';
import { useConsent } from './hooks/useConsent';
import { useFeatures } from './hooks/useFeatures';
import stateManager from './services/stateManager';
import * as mixpanelService from './services/mixpanelService';
// --- Eagerly loaded: everything the first painted screen actually needs ------
// The chat view is the landing screen for a signed-in user, so its chrome ships
// in the initial chunk. Everything else is code-split below.
import Header from './components/Header';
import ChatArea from './components/ChatArea';
import InputSection from './components/InputSection';
import SuggestionChips from './components/SuggestionChips';
import ProgressBar from './components/ProgressBar';
import HamburgerMenu from './components/HamburgerMenu';
import FeedbackButton from './components/FeedbackButton';
import UpdateNotification from './components/UpdateNotification';
// Eager on purpose: this is what renders *while* a lazy chunk downloads, so it
// cannot itself be lazy. It costs nothing extra — ChatArea → ThinkingIndicator
// already pulls Loader (and Loader.module.css) into the initial chunk.
import Loader from './components/Loader';
import BrandLoadingScreen from './components/BrandLoadingScreen';
// Shared secondary top bar (UX-12). Eager: tiny, and used by the CALENDLY view
// which renders inline in this file rather than as a lazy chunk.
import PageHeader from './components/PageHeader';
// Eager: renders nothing except on the ?checkout=success return from Stripe.
import CheckoutReturn from './components/CheckoutReturn';
// Eager: renders nothing except on the ?coaching_checkout=success return
// (purchased coaching-session credits).
import CoachingCheckoutReturn from './components/CoachingCheckoutReturn';
import BookingConfirmed from './components/BookingConfirmed';
import { authAPI, todosAPI, sessionsAPI, contentAPI, beadsAPI, meetSophiaAPI, familyAPI } from './services/api';
import { RESERVED_PATHS, parseDeepLink, savePendingDeepLink, consumePendingDeepLink } from './utils/deepLink';
import { isDevHost } from './utils/devHost';
import styles from './styles/App.module.css';

// --- Code-split (PERF-04) ---------------------------------------------------
// Every screen/modal below is reachable only after an explicit user action (or
// on a public URL that the app itself never renders), so it belongs in a child
// chunk rather than the initial bundle. Grouped by why it is split.

// Public / pre-auth pages — rendered only for a matching pathname.
const LandingPage = lazy(() => import('./components/LandingPage'));
const OrganizationLanding = lazy(() => import('./components/OrganizationLanding'));
const MeetSophia = lazy(() => import('./components/MeetSophia'));
const Workshop = lazy(() => import('./components/Workshop'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const LoginForm = lazy(() => import('./components/LoginForm'));

// Auth / first-run gates — each shown to a small subset of loads.
const AccessRestricted = lazy(() => import('./components/AccessRestricted'));
const FirstTimeConsent = lazy(() => import('./components/FirstTimeConsent'));
const AppCodeInput = lazy(() => import('./components/AppCodeInput'));
const OnboardingScreen = lazy(() => import('./components/OnboardingScreen'));

// Secondary screens reached from the menu / progress bar / deep links.
const TodoList = lazy(() => import('./components/TodoList'));
const ProgressDashboard = lazy(() => import('./components/ProgressDashboard'));
const Bracelet = lazy(() => import('./components/Bracelet'));
const ArticleDetailView = lazy(() => import('./components/ArticleDetailView'));
const Quiz = lazy(() => import('./components/Quiz'));
const DeepLinkVideo = lazy(() => import('./components/DeepLinkVideo'));
const NewsDetailView = lazy(() => import('./components/NewsDetailView'));
const AllLearningResourcesView = lazy(() => import('./components/AllLearningResourcesView'));
const AllNewsView = lazy(() => import('./components/AllNewsView'));
const SettingsScreen = lazy(() => import('./components/SettingsScreen'));
const SchedulingScreen = lazy(() => import('./components/SchedulingScreen'));

// Sophia Together (circles / money dates / family) — opt-in surfaces.
const MoneyDatesScreen = lazy(() => import('./components/MoneyDatesScreen'));
const CirclesScreen = lazy(() => import('./components/CirclesScreen'));
const CircleChatArea = lazy(() => import('./components/CircleChatArea'));
const CircleTopBand = lazy(() => import('./components/CircleTopBand'));
const CircleSettingsScreen = lazy(() => import('./components/CircleSettingsScreen'));
const CreateCircleFlow = lazy(() => import('./components/CreateCircleFlow'));
const FamilyPlanScreen = lazy(() => import('./components/FamilyPlanScreen'));
const MockCheckout = lazy(() => import('./components/MockCheckout'));

// Views that disappear with the ENABLE_CIRCLES kill switch (see useFeatures).
const SOPHIA_TOGETHER_VIEWS = [VIEWS.CIRCLES, VIEWS.CIRCLE_SETTINGS, VIEWS.FAMILY];

// Views that disappear with the ENABLE_COACHES kill switch (see useFeatures).
const COACHING_VIEWS = [VIEWS.CALENDLY, VIEWS.COACHING_SCHEDULE, VIEWS.COACHING_SESSION, VIEWS.COACH_DASHBOARD];

// Internal/admin dashboards — never opened by a normal user, and the heaviest
// dependencies in the app live here (@nivo charts, react-wordcloud/d3-cloud).
const CoachDashboard = lazy(() => import('./components/CoachDashboard'));
const OrganizationDashboard = lazy(() => import('./components/OrganizationDashboard'));
const DevDashboard = lazy(() => import('./components/DevDashboard'));
const CommunityHub = lazy(() => import('./components/CommunityHub'));

// Modals / banners rendered only while their trigger state is set.
const PWAInstallPrompt = lazy(() => import('./components/PWAInstallPrompt'));
const NotificationPermissionModal = lazy(() => import('./components/NotificationPermissionModal'));
const TodoCompletionModal = lazy(() => import('./components/TodoCompletionModal'));
const FeedbackModal = lazy(() => import('./components/FeedbackModal'));
const SessionBanner = lazy(() => import('./components/SessionBanner'));
const SessionDetailsScreen = lazy(() => import('./components/SessionDetailsScreen'));
const BeadUnlockModal = lazy(() => import('./components/BeadUnlockModal'));
const AnalyticsPrompt = lazy(() => import('./components/AnalyticsPrompt'));

// The product tour library (react-joyride + react-floater + popper) is only
// needed when a tour actually runs, so it is mounted on demand.
const Joyride = lazy(() => import('react-joyride'));

// react-joyride's STATUS/ACTIONS literals, inlined so the tour bundle stays out
// of the initial chunk (importing the named exports would pull the whole
// library back in). Mirrors src/literals in react-joyride.
const JOYRIDE_STATUS_FINISHED = 'finished';
const JOYRIDE_STATUS_SKIPPED = 'skipped';
const JOYRIDE_ACTION_CLOSE = 'close';

// How long a chunk may take before the wait is worth acknowledging. Same-origin
// chunks off a warm cache land in tens of milliseconds; a spinner that appears
// and vanishes inside that window is pure flicker, and reads as jank rather than
// as feedback. Past ~1/4 second a blank screen starts to read as broken instead.
const FALLBACK_DELAY_MS = 300;

/**
 * Shown while a lazily-loaded chunk downloads. Two phases, deliberately.
 *
 * Phase 1 (first FALLBACK_DELAY_MS): silent reserved space only. Nothing
 * animates, nothing is announced — for the common fast chunk the screen simply
 * appears, which is the correct "loading" experience for a sub-300ms wait.
 * `aria-hidden` is right here precisely *because* there is no status yet.
 *
 * Phase 2: the real Loader, which carries its own role="status"/aria-live — so
 * it is never wrapped in anything aria-hidden. Loader's fullscreen variant is
 * itself min-height:100vh, matching the phase-1 spacer, so the swap moves
 * nothing on the page.
 *
 * Used ONLY where the whole viewport would otherwise be blank. Boundaries that
 * swap a region inside an already-painted screen (the circle band, the session
 * banner, the circle chat body) and every modal keep `fallback={null}`: the
 * header, plan bar and composer stay on screen throughout, so the user already
 * has proof the app is alive and a spinner there would add motion without
 * adding information.
 */
function ScreenFallback() {
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setWaited(true), FALLBACK_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!waited) {
    return <div style={{ flex: 1, minHeight: '100vh' }} aria-hidden="true" />;
  }

  return <Loader variant="fullscreen" indicator="dots" text="Loading…" />;
}

// Helper function to format session time for display
function formatSessionTime(isoString) {
  const date = new Date(isoString);
  const day = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} at ${time}`;
}

// Paths that must NOT trigger the unauthenticated auto-redirect: public pages
// that have their own early-return rendering (/landing, /privacy, /tos), the
// logout flow, and per-organization landing pages (a single non-reserved path
// segment). Everything else for an unauthenticated visitor is routed by the
// effect below — to sophiawomen.com, or into Thinkific OAuth for /login & deep
// links — because the marketing landing page is deprecated.
function isAutoSignInExemptPath(pathname) {
  if (['/landing', '/privacy', '/tos', '/logout', '/meet-sophia'].includes(pathname)) return true;
  // Public workshop content pages: /workshop/{slug}
  if (pathname.startsWith('/workshop/')) return true;
  const orgSlug = pathname.match(/^\/([A-Za-z0-9][A-Za-z0-9_-]*)\/?$/);
  return !!(orgSlug && !RESERVED_PATHS.includes(orgSlug[1].toLowerCase()));
}

// Set in sessionStorage right before a silent (prompt=none) SSO redirect from the
// bare root "/", and read on the bounce-back so a silent failure (no live
// Thinkific session) falls through to the marketing site instead of the Thinkific
// login page. Same-browser round-trip, so sessionStorage survives it.
const SILENT_SSO_FLAG = 'sophia_silent_sso';

// REACT_APP_AUTH_PROVIDER=local → Strapi email+password auth: no Thinkific
// redirects anywhere; unauthenticated visitors get the in-app LoginForm
// (with register / forgot / reset). Default: Thinkific OAuth, unchanged.
const IS_LOCAL_AUTH = process.env.REACT_APP_AUTH_PROVIDER === 'local';

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login, register, resetPassword, logout, updateUser, isLoading: authLoading } = useAuth();
  const {
    messages,
    isLoading: chatLoading,
    conversationId,
    currentMission,
    hasMoreMessages,
    isLoadingMore,
    suggestions,
    sendMessage,
    sendSuggestion,
    resetChat,
    loadHistory,
    loadAllMessages,
    liveMessageIds
  } = useChat();
  const { isInstallable, promptInstall, dismissPrompt, isIOS } = usePWAInstall();
  // Session-only hide when the user scrolls down past the install banner —
  // softer than dismissPrompt (which suppresses it for 7 days): an implicit
  // scroll shouldn't cost a week of install prompts.
  const [installBannerScrolledAway, setInstallBannerScrolledAway] = useState(false);

  // Mobile keyboard detection: when the on-screen keyboard is up, the whole
  // top-bar stack slides away so the conversation stays readable (product
  // direction). visualViewport.height dropping well below its recent maximum
  // is the keyboard; a simultaneous width change is a rotation/resize and
  // resets the baseline instead.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    let baseH = vv.height;
    let baseW = vv.width;
    const onResize = () => {
      if (Math.abs(vv.width - baseW) > 50) {
        baseW = vv.width;
        baseH = vv.height;
        setKeyboardOpen(false);
        return;
      }
      if (vv.height > baseH) baseH = vv.height;
      setKeyboardOpen(baseH - vv.height > 150);
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);
  const {
    needsConsent,
    analyticsConsent,
    loading: consentLoading,
    recordConsent,
    checkConsent
  } = useConsent();

  // Local auth logs in WITHOUT a page reload, so the mount-time consent check
  // ran unauthenticated (needsConsent=false). Re-check whenever the session
  // user changes (in-session register / login / password reset), or a brand
  // new account would sail past the compliance screen entirely.
  useEffect(() => {
    if (user?.id) checkConsent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  const {
    requestPermission: requestNotificationPermission,
    showBanner: showNotificationBanner,
    dismissBanner: dismissNotificationBanner
  } = useNotifications(messages, user, needsConsent);
  const { currentView, viewData, navigateTo, replaceTo, goBack, goHome, backGuard } = useNavigation(VIEWS.CHAT);
  const {
    shouldShowOnboarding,
    flowItems,
    flowType,
    startOnboarding,
    completeOnboarding
  } = useOnboardingFlow(user);

  // Server-driven feature flags. ENABLE_CIRCLES=false on the server removes
  // Sophia Together from the app entirely: the Circles nav slot, both menu
  // entries, the screens, the upsell, and /circle/{token} invite links.
  // ENABLE_COACHES (off unless 'true') gates human coaching the same way: the
  // Coach nav slot, the coach views, the session poll, and the dashboard entry.
  const { circles: circlesEnabled, coaches: coachesEnabled } = useFeatures();

  // Circles (Sophia Together): activeCircleId !== null means the main chat is
  // showing a group circle instead of the user's personal chat.
  const {
    circles: myCircles,
    activeCircleId,
    activeCircle,
    enterCircle,
    exitToPersonal,
    markRead: markCircleRead,
    refreshCircles: refreshCirclesList,
    refreshActive: refreshActiveCircle
  } = useCircles(!!user && circlesEnabled);
  const circleChat = useCircleChat(activeCircleId);

  // Circle chat is a MODE (not a view), so it joins the hardware-Back
  // behaviour through the navigation back-guard: entering a circle arms the
  // guard; Back with no views to pop exits the circle to the personal chat
  // instead of leaving the page.
  useEffect(() => {
    backGuard.rootPending = !!activeCircleId;
    backGuard.rootPop = activeCircleId
      ? () => { exitToPersonal(); return true; }
      : null;
    if (activeCircleId) backGuard.arm();
  }, [activeCircleId, exitToPersonal, backGuard]);
  const [showCreateCircle, setShowCreateCircle] = useState(false);
  const [circleUpsell, setCircleUpsell] = useState(null); // null | 'use' | 'create' — Sophia Together upsell

  // While viewing a circle, keep it marked read as new messages arrive (so its
  // switcher badge stays clear; other circles accrue unread in the background).
  const markedCircleRef = useRef({ id: null, maxId: 0 });
  useEffect(() => {
    if (!activeCircleId) return;
    const msgs = circleChat.messages;
    if (!msgs || msgs.length === 0) return;
    const maxId = msgs.reduce((mx, x) => Math.max(mx, x.id || 0), 0);
    const ref = markedCircleRef.current;
    if (ref.id !== activeCircleId) { ref.id = activeCircleId; ref.maxId = 0; }
    if (maxId > ref.maxId) {
      ref.maxId = maxId;
      markCircleRead(activeCircleId, maxId);
    }
  }, [activeCircleId, circleChat.messages, markCircleRead]);

  // Family & Friends status — drives the circle→family "add to my plan" checkbox
  // and self-heals any pending seat invited to this user's email (getFamily
  // reconciles server-side).
  const [familyStatus, setFamilyStatus] = useState(null);
  useEffect(() => {
    if (!user || !circlesEnabled) { setFamilyStatus(null); return; }
    familyAPI.getFamily().then(setFamilyStatus).catch(() => {});
  }, [user, circlesEnabled]);
  const familyEnabled = familyStatus?.accountType === 'family';

  // Circle entitlements. You can CREATE circles only with Sophia Together (a
  // family account — bought, or granted by an org). You can USE circles either
  // way: owning Sophia Together, or simply having been invited into one.
  // Both are hard-false when the whole feature is switched off.
  const canCreateCircle = circlesEnabled && familyEnabled;
  const canUseCircles = circlesEnabled && (canCreateCircle || (myCircles && myCircles.length > 0));

  const handleCreateCircle = () => {
    if (!circlesEnabled) return;
    if (!canCreateCircle) { setCircleUpsell('create'); return; }
    setShowCreateCircle(true);
  };

  // Open circles from the nav: locked → Sophia Together upsell; otherwise enter
  // your most recent circle, or (a family account with none yet) start one.
  const handleOpenCircles = () => {
    if (!circlesEnabled) return;
    if (!canUseCircles) { setCircleUpsell('use'); return; }
    if (myCircles && myCircles.length > 0) {
      enterCircle(myCircles[0].id);
      goHome();
    } else {
      handleCreateCircle();
    }
  };

  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [justSwitchedFromTodos, setJustSwitchedFromTodos] = useState(false);
  const [hasSeenCompletionModal, setHasSeenCompletionModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [readingSession, setReadingSession] = useState(null); // Track content reading { contentType, title, startTime }
  const [visitingScreen, setVisitingScreen] = useState(null); // Track screen visits for context { screenType, startTime, data }
  const [revealedMessageId, setRevealedMessageId] = useState(null); // Last assistant message whose reveal animation finished — chips wait on this

  // Once a live reply's word-by-word reveal finishes, the user has read it —
  // retire its id from liveMessageIds so that when ChatArea remounts (Tasks
  // screen and back, etc.) the message renders all at once. Replaying the
  // animation made users think a different answer had arrived.
  const handleMessageRevealComplete = useCallback((id) => {
    setRevealedMessageId(id);
    liveMessageIds.delete(id);
  }, [liveMessageIds]);
  const [scrollToBottomTrigger, setScrollToBottomTrigger] = useState(0); // Trigger to jump the chat to the latest message when user sends one
  const [progressBarKey, setProgressBarKey] = useState(0); // Force ProgressBar remount on objective change
  const [hideProgressBar, setHideProgressBar] = useState(false); // Hide during objective change to prevent stale data
  const [upcomingSession, setUpcomingSession] = useState(null); // Track upcoming coaching session for banner
  const [selectedCoachForScheduling, setSelectedCoachForScheduling] = useState(null); // Coach selected for scheduling
  const [justBookedSession, setJustBookedSession] = useState(null); // Session just booked → full-screen "Your session is booked" confirmation
  const [canJoinSession, setCanJoinSession] = useState(false); // Track if session is joinable

  // --- Deep linking (email links to a quiz / article / video) ---------------
  // Capture: if the app booted on a deep-link URL, stash it before any login
  // redirect so it survives the Thinkific OAuth round-trip (same browser, so
  // localStorage persists). Runs once on mount.
  useEffect(() => {
    if (parseDeepLink(window.location.pathname)) {
      savePendingDeepLink(window.location.pathname);
    }
  }, []);

  // Apply: once the user is fully through auth + all gates, open the linked
  // content. New users still inside consent/app-code/onboarding gates have the
  // link discarded (per product decision: deep links are ignored for them).
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    // Wait for auth + consent to finish resolving.
    if (!user || authLoading || consentLoading) return;

    const gated = needsConsent !== false || !user.hasRedeemedCode || shouldShowOnboarding;
    const pendingPath = consumePendingDeepLink();
    deepLinkAppliedRef.current = true; // single-shot, regardless of outcome

    if (gated) return; // new/incomplete user - ignore the deep link

    const parsed = parseDeepLink(pendingPath || window.location.pathname);
    if (!parsed) return;
    // A /circle/{token} invite link is dead while the feature is switched off:
    // drop the user on the chat rather than a screen she can't use.
    if (!circlesEnabled && SOPHIA_TOGETHER_VIEWS.includes(parsed.view)) {
      if (window.location.pathname !== '/') {
        window.history.replaceState({}, document.title, '/');
      }
      return;
    }

    navigateTo(parsed.view, parsed.data);
    if (window.location.pathname !== '/') {
      window.history.replaceState({}, document.title, '/');
    }
  }, [user, authLoading, consentLoading, needsConsent, shouldShowOnboarding, navigateTo, circlesEnabled]);

  // --- Meet Sophia adoption --------------------------------------------------
  // If this user completed the pre-signup /meet-sophia session, adopt the EXACT
  // previewed plan into a real conversation BEFORE onboarding computes its
  // steps — the goal/barrier/plan questions are then skipped automatically and
  // the plan she saw is the plan she gets. Cheap server no-op for everyone
  // else. Waits for the same gates as onboarding (consent + app code) so no
  // content is created for her before she's accepted the terms.
  const [meetSophiaAdoptPending, setMeetSophiaAdoptPending] = useState(true);
  const meetSophiaAdoptRef = useRef(false);
  useEffect(() => {
    if (meetSophiaAdoptRef.current) return;
    if (!user || authLoading || consentLoading) return;
    if (needsConsent !== false || !user.hasRedeemedCode) return;
    meetSophiaAdoptRef.current = true;

    if (user.hasActivePlan !== false) {
      setMeetSophiaAdoptPending(false);
      return;
    }

    (async () => {
      try {
        // Race a hard timeout: fetchAPI has no AbortController, and a hung
        // /adopt would otherwise leave the pre-onboarding splash up forever.
        // Adoption includes an LLM prompt-generation call, so be generous.
        const result = await Promise.race([
          meetSophiaAPI.adopt(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Meet Sophia adoption timed out')), 15000)
          ),
        ]);
        if (result?.adopted) {
          console.log('[App] Meet Sophia session adopted — reloading with her plan in place');
          // Reload so the refreshed user (active plan + mission + barriers)
          // drives a much shorter onboarding.
          window.location.reload();
          return;
        }
      } catch (err) {
        // Non-fatal: onboarding proceeds. A server-side failure released the
        // adoption claim, so the next app load retries automatically.
        console.warn('[App] Meet Sophia adoption check failed:', err);
      }
      setMeetSophiaAdoptPending(false);
    })();
  }, [user, authLoading, consentLoading, needsConsent]);

  // Bead unlock modal queue - shows beads one at a time
  const [beadQueue, setBeadQueue] = useState([]);
  const [currentBeadModal, setCurrentBeadModal] = useState(null);
  const beadQueueChecked = useRef(false);
  const shownBeadSlugs = useRef(new Set()); // Track all beads shown or queued to prevent duplicates

  // Shared function to check for unviewed beads and queue modals
  const checkUnviewedBeads = useCallback(async () => {
    if (!user) return;
    try {
      const response = await beadsAPI.getUnviewedBeads();
      const beads = response?.beads || [];
      if (beads.length > 0) {
        console.log(`[App] Found ${beads.length} unviewed bead(s) - queuing modals`);
        const beadModals = beads.map(b => ({
          userBeadId: b.userBeadId,
          bead: {
            id: b.beadId,
            bead_name: b.bead_name,
            bead_slug: b.bead_slug,
            bead_tier: b.bead_tier,
            unlock_story: b.unlock_story,
            bead_image: b.bead_image,
            reveal_video: b.reveal_video
          },
          unlockStory: b.unlock_story || `You earned the ${b.bead_name} bead!`,
          awardedReason: b.awarded_reason || null
        }));
        setBeadQueue(prev => {
          // Deduplicate: skip beads already shown, currently showing, or in queue
          const existingSlugs = new Set(prev.map(b => b.bead?.bead_slug));
          const newModals = beadModals.filter(b => {
            const slug = b.bead?.bead_slug;
            return !existingSlugs.has(slug) && !shownBeadSlugs.current.has(slug);
          });
          newModals.forEach(b => shownBeadSlugs.current.add(b.bead?.bead_slug));
          return newModals.length > 0 ? [...prev, ...newModals] : prev;
        });
      }
    } catch (error) {
      console.error('[App] Failed to check unviewed beads:', error);
    }
  }, [user]);

  // Show next bead from queue as soon as the current modal is dismissed.
  // Beads surface IMMEDIATELY when awarded (product direction) — even while
  // Sophia is still mid-reply. The old gating on the thinking overlay is gone
  // along with the overlay itself: the chat now waits inline, so there is
  // nothing for the modal to stack over.
  useEffect(() => {
    if (!currentBeadModal && beadQueue.length > 0) {
      const [next, ...rest] = beadQueue;
      setCurrentBeadModal(next);
      setBeadQueue(rest);
    }
  }, [currentBeadModal, beadQueue]);

  // Listen for real-time bead awarded events and add to queue
  useEffect(() => {
    const handleBeadAwarded = (event) => {
      const bead = event.detail;
      console.log('[App] 🎁 Bead awarded event received:', bead);
      const slug = bead.beadSlug;
      const beadModal = {
        userBeadId: bead.userBeadId,
        bead: {
          id: bead.beadId || bead.userBeadId,
          bead_name: bead.beadName,
          bead_slug: slug,
          bead_tier: bead.tier,
          unlock_story: bead.unlockStory,
          bead_image: bead.imageUrl
        },
        unlockStory: bead.unlockStory || `You earned the ${bead.beadName} bead!`,
        awardedReason: bead.reason || null
      };

      // Add to queue only if not already shown or queued (deduplicate by slug)
      if (shownBeadSlugs.current.has(slug)) {
        console.log(`[App] Bead ${slug} already shown, skipping duplicate`);
        return;
      }
      shownBeadSlugs.current.add(slug);
      setBeadQueue(prev => {
        const alreadyQueued = prev.some(b => b.bead?.bead_slug === slug);
        if (alreadyQueued) {
          console.log(`[App] Bead ${slug} already in queue, skipping duplicate`);
          return prev;
        }
        return [...prev, beadModal];
      });
    };

    const handleProfileAnswer = (event) => {
      const { label, field } = event.detail;
      if (label && sendMessage) {
        // Send as hidden message — user already sees answer in the card
        sendMessage(`[Profile answer for ${field}]: ${label}`, null, { isHidden: true });
      }
    };

    window.addEventListener('beadAwarded', handleBeadAwarded);
    window.addEventListener('checkBeads', checkUnviewedBeads);
    window.addEventListener('profileQuestionAnswered', handleProfileAnswer);
    return () => {
      window.removeEventListener('beadAwarded', handleBeadAwarded);
      window.removeEventListener('checkBeads', checkUnviewedBeads);
      window.removeEventListener('profileQuestionAnswered', handleProfileAnswer);
    };
  }, [checkUnviewedBeads, sendMessage]);

  // Onboarding tour state
  const [runTour, setRunTour] = useState(false);
  // react-joyride lives in its own chunk (see the lazy import above). Latch on
  // the first tour start so the library is fetched only when a tour actually
  // runs, and stays mounted afterwards (Joyride's own `run` prop then controls
  // visibility, exactly as before).
  const [tourMounted, setTourMounted] = useState(false);
  useEffect(() => {
    if (runTour) setTourMounted(true);
  }, [runTour]);
  // Once the auto-start effect has fired (or the tour has ended) within this
  // session, never auto-start again. Joyride's callback fires "finished" many
  // times, and the updateUser({sawTutorial:true}) round-trip is async — without
  // this ref the effect re-runs (deps shift while user state is stale) and the
  // tour restarts immediately. Manual trigger ignores this ref.
  const tourAutoHandledRef = useRef(false);
  // Reset when each new tour run begins; flipped true on first 'finished'
  // callback so we don't double-fire setState / updateUser.
  const tourFinishHandledRef = useRef(false);

  // Build tour steps - only targets elements visible without opening the menu.
  // Step order: input, progress, session (only when coaching is enabled —
  // its Coach-slot target doesn't exist otherwise), hamburger, feedback.
  const tourSteps = useMemo(() => [
    {
      target: '[data-tour="input-section"]',
      content: (
        <>
          <h3>Chat with Sophia</h3>
          <p>Ask me anything about personal finance, investing, or your financial goals.</p>
        </>
      ),
      placement: 'top',
      disableBeacon: true,
    },
    {
      target: '[data-tour="progress-bar"]',
      content: (
        <>
          <h3>Your Action Plan</h3>
          <p>Track your progress through personalised financial milestones. Tap to see all your tasks.</p>
        </>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    ...(coachesEnabled ? [{
      target: '[data-tour="session-indicator"]',
      content: (
        <>
          <h3>Book a Coaching Session</h3>
          <p>Tap here to book a one-on-one session with a financial coach.</p>
        </>
      ),
      placement: 'bottom',
      disableBeacon: true,
    }] : []),
    {
      target: '[data-tour="hamburger-menu"]',
      content: (
        <>
          <h3>Navigation Menu</h3>
          <p>Access your action plan, beads, learning resources, and more.</p>
        </>
      ),
      placement: 'bottom-start',
      disableBeacon: true,
    },
    {
      target: '[data-tour="feedback-button"]',
      content: (
        <>
          <h3>Report Issues</h3>
          <p>Found a bug or have feedback? Use this button on the left to let us know.</p>
        </>
      ),
      placement: 'right',
      disableBeacon: true,
    },
  ], [coachesEnabled]);

  // 1 second startup delay to avoid using resources during testing
  const [startupDelay, setStartupDelay] = useState(true);

  // Refs to prevent duplicate Mixpanel tracking
  const hasTrackedAppOpened = useRef(false);
  const lastTrackedView = useRef(null);

  // Viewport height handler for mobile orientation changes AND the on-screen
  // keyboard. Publishes two custom properties:
  //   --vh              1% of the height actually VISIBLE to the user
  //   --keyboard-inset  how much of the layout viewport the keyboard covers
  //
  // window.innerHeight is the wrong source on iOS: Safari does not shrink the
  // layout viewport when the keyboard opens, it just scrolls the page under it.
  // Sizing the app shell off innerHeight therefore left the bottom of the
  // conversation — the newest messages, the ones being replied to — parked
  // behind the keyboard, which is the "last messages disappear while typing"
  // report. visualViewport reports the real visible band, so the shell shrinks
  // to it and the chat keeps its scroll-to-bottom in view.
  useEffect(() => {
    const vv = window.visualViewport;

    const updateViewportHeight = () => {
      // offsetTop is how far Safari has already scrolled the layout viewport
      // out of sight; it has to come off the visible height too, otherwise the
      // shell is taller than the band the user can actually see.
      const visibleHeight = vv ? vv.height : window.innerHeight;
      document.documentElement.style.setProperty('--vh', `${visibleHeight * 0.01}px`);

      // What the keyboard (or any other on-screen widget) covers at the bottom.
      // The fixed input bar sits on top of this so it never hides under the keys.
      const covered = vv
        ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
        : 0;
      document.documentElement.style.setProperty('--keyboard-inset', `${covered}px`);

      // Undo the pan iOS applies to the layout viewport when it scrolls a
      // focused field into view — left in place it shifts the whole app up and
      // sideways, which is what pushed the fixed overlays off-centre and cut
      // the send button off the right edge. Skipped while the user has
      // pinch-zoomed in (scale > 1), because there the pan is theirs and
      // resetting it would break zooming, which we support on purpose.
      // Only for the app shell, which locks html/body to the viewport and never
      // scrolls the document itself. The public pages (workshop, meet-sophia,
      // org landing) deliberately re-enable page scrolling - there, resetting
      // to 0 would throw the reader back to the top of a long page every time
      // the keyboard opened.
      const documentScrolls = document.documentElement.scrollHeight > window.innerHeight + 1;
      if (vv && !documentScrolls && Math.abs(vv.scale - 1) < 0.01 && (vv.offsetTop > 0 || vv.offsetLeft > 0)) {
        window.scrollTo(0, 0);
      }
    };

    // Set initial value
    updateViewportHeight();

    // Update on resize
    window.addEventListener('resize', updateViewportHeight);
    if (vv) {
      vv.addEventListener('resize', updateViewportHeight);
      vv.addEventListener('scroll', updateViewportHeight);
    }

    // Update on orientation change with delay for browser to settle
    const handleOrientationChange = () => {
      setTimeout(updateViewportHeight, 100);
    };
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (vv) {
        vv.removeEventListener('resize', updateViewportHeight);
        vv.removeEventListener('scroll', updateViewportHeight);
      }
    };
  }, []);

  // Entry routing for unauthenticated visitors (the marketing landing page is
  // deprecated). With no valid session, behaviour splits by entry path:
  //   • "/" (bare root) → SILENT Thinkific SSO (prompt=none). If the visitor
  //     still has a live Thinkific session they're logged straight into the app
  //     with no UI; otherwise Thinkific bounces back with ?error=login_required
  //     (no page shown) and we send them to the marketing site, sophiawomen.com.
  //     → "try to log in; if it fails, go to .com".
  //   • everything else (/login, email deep links, unknown paths) → INTERACTIVE
  //     Thinkific OAuth, i.e. the Thinkific login page where the user enters creds.
  // A signed-in visitor (valid token → `user` set by useAuth) just loads the app.
  // Bounce-back (?error=… / ?token=…): a `token` is consumed by useAuth (sets
  // `user`, effect returns above). An `error` is routed by how the attempt
  // started — a failed SILENT attempt (flagged in sessionStorage before the
  // redirect) → marketing site; a failed INTERACTIVE attempt → back to the
  // Thinkific login page. Dev shows the email/password LoginForm (rendered in the
  // !user block below) since there's no local OAuth.
  const autoRedirectRef = useRef(false);
  useEffect(() => {
    if (authLoading) return; // wait until we know whether a session exists
    if (user) {
      // Signed in — clear any stale silent-SSO marker and load the app.
      try { sessionStorage.removeItem(SILENT_SSO_FLAG); } catch (e) { /* ignore */ }
      return;
    }
    if (isAutoSignInExemptPath(location.pathname)) return;

    if (IS_LOCAL_AUTH) return; // local auth: LoginForm is rendered in the !user block
    if (isDevHost()) return; // dev: LoginForm is rendered in the !user block

    if (autoRedirectRef.current) return; // guard against double-invocation
    autoRedirectRef.current = true;

    const serverUrl = process.env.REACT_APP_API_URL || 'https://sophia-server-production.up.railway.app';
    const params = new URLSearchParams(location.search);
    const cameFromAuth = params.has('error') || params.has('token'); // sign-in bounce-back

    if (cameFromAuth) {
      // Reached here only with an `error` (a `token` is handled by useAuth, which
      // sets `user` so the effect returns above). Route the failure by how the
      // attempt started.
      let wasSilent = false;
      try {
        wasSilent = sessionStorage.getItem(SILENT_SSO_FLAG) === '1';
        sessionStorage.removeItem(SILENT_SSO_FLAG);
      } catch (e) { /* ignore */ }

      if (wasSilent) {
        // Silent root SSO found no Thinkific session → marketing site.
        window.location.href = 'https://www.sophiawomen.com';
      } else {
        // Interactive login was cancelled/errored → back to the Thinkific login page.
        window.location.href = `${serverUrl}/api/auth/oauth/initiate`;
      }
      return;
    }

    if (location.pathname === '/') {
      // Bare root → silent SSO. Flag it so the bounce-back (above) sends a
      // no-session failure to the marketing site rather than the login page.
      try { sessionStorage.setItem(SILENT_SSO_FLAG, '1'); } catch (e) { /* ignore */ }
      window.location.href = `${serverUrl}/api/auth/oauth/initiate?prompt=none`;
      return;
    }

    // Everything else (/login, email deep links, unknown paths) → interactive
    // Thinkific OAuth (the login page). Deep links were already stashed on mount
    // and are re-applied once auth completes.
    let authUrl = `${serverUrl}/api/auth/oauth/initiate`;
    // If the user just logged out, force Thinkific to re-prompt for credentials
    // (otherwise the still-active Thinkific session silently logs them back in).
    try {
      if (sessionStorage.getItem('sophia_force_reauth')) {
        sessionStorage.removeItem('sophia_force_reauth');
        authUrl += '?prompt=login';
      }
    } catch (e) { /* ignore */ }
    window.location.href = authUrl;
  }, [authLoading, user, location.pathname, location.search]);

  // An EXISTING, logged-in user opened an organization's register link
  // (/register?org={slug}): attach the membership to their account right here
  // — no re-registration — then reload with a clean URL so access gating
  // re-evaluates as org-covered. (Unauthenticated visitors never reach this:
  // LoginForm handles the org context through register/login instead.)
  const orgJoinRef = useRef(false);
  useEffect(() => {
    if (!user || orgJoinRef.current) return;
    const orgSlug = new URLSearchParams(location.search).get('org');
    if (!orgSlug) return;
    orgJoinRef.current = true;
    (async () => {
      try {
        await authAPI.joinOrg(orgSlug);
        window.location.replace('/');
      } catch (err) {
        console.error('[App] join-org failed:', err.message);
        // Invalid/restricted link — just clean the URL and carry on.
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    })();
  }, [user, location.search]);

  // Handle /logout route
  useEffect(() => {
    if (window.location.pathname.toLowerCase() === '/logout') {
      console.log('[App] /logout route detected - logging out');
      // logout() needs the auth token for the server call (which returns the Thinkific
      // sign-out URL we then navigate to), so clear storage only after it finishes.
      logout().finally(() => localStorage.clear());
      // Clean up URL
      window.history.replaceState(null, '', '/');
    }
  }, [logout]);

  // Check if there's a pending plan (TodoPlan tag in last message)
  // Memoized to avoid recalculating on every render
  const hasPendingPlan = useMemo(() => {
    return messages.length > 0 &&
      messages[messages.length - 1].content?.includes('<TodoPlan:');
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setStartupDelay(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Check for unviewed beads on app load
  useEffect(() => {
    if (!user || startupDelay || beadQueueChecked.current) return;
    beadQueueChecked.current = true;
    checkUnviewedBeads();
  }, [user, startupDelay, checkUnviewedBeads]);

  // Re-check for unviewed beads when returning to chat from another screen
  const prevVisitingScreen = useRef(visitingScreen);
  useEffect(() => {
    if (prevVisitingScreen.current && !visitingScreen) {
      // User just returned to chat — check for any new beads
      checkUnviewedBeads();
    }
    prevVisitingScreen.current = visitingScreen;
  }, [visitingScreen, checkUnviewedBeads]);

  // Fetch upcoming coaching session for banner. Skipped entirely when human
  // coaching is switched off (ENABLE_COACHES) — the route 404s server-side.
  // The server only returns sessions that haven't ENDED yet (a late arrival
  // must still get in), so whatever it hands us is live; null clears. Also
  // called on demand: entering the session screen, the screen noticing the
  // session ended, or tapping the Coach tab while on it.
  const refreshUpcomingSession = useCallback(async () => {
    if (!coachesEnabled || !user) return;
    try {
      const response = await sessionsAPI.getNextSession();
      setUpcomingSession(response?.session || null);
    } catch (err) {
      console.error('[App] Failed to fetch upcoming session:', err);
    }
  }, [coachesEnabled, user]);

  useEffect(() => {
    if (!coachesEnabled) {
      setUpcomingSession(null);
      return;
    }
    if (!startupDelay) {
      refreshUpcomingSession();
      // Refresh every 5 minutes
      const interval = setInterval(refreshUpcomingSession, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [startupDelay, coachesEnabled, refreshUpcomingSession]);

  // Fresh data whenever the session details screen is opened
  useEffect(() => {
    if (currentView === VIEWS.COACHING_SESSION) {
      refreshUpcomingSession();
    }
  }, [currentView, refreshUpcomingSession]);

  // Auto-start tour at most once per user lifetime.
  // Gated on User.sawTutorial — flipped to true when the tour finishes/skips
  // (see handleJoyrideCallback). There is no manual replay entry point.
  useEffect(() => {
    if (!user || shouldShowOnboarding || startupDelay) return;
    if (user.sawTutorial) return;
    if (tourAutoHandledRef.current) return;

    // Only start tour when all required elements are likely to be present:
    // - Chat view is active
    // - Chat is not loading
    // - We have a conversationId (so ProgressBar can render)
    // - No pending plan (so InputSection can render)
    // - Messages are loaded (so chat is ready)
    if (currentView !== VIEWS.CHAT ||
        chatLoading ||
        !conversationId ||
        hasPendingPlan ||
        messages.length === 0) {
      return;
    }

    // One interruption at a time. The tour renders *beneath* the notification
    // modal, so starting it while that modal (or the install prompt) is up
    // produces a tour the user can see but cannot click or close.
    if (showNotificationBanner || isInstallable) {
      return;
    }

    let cancelled = false;

    // Verify all tour target elements are in the DOM before starting
    let retryCount = 0;
    const maxRetries = 5; // Try for up to 5 seconds (5 retries * 1 second)

    const checkElementsAndStartTour = () => {
      if (cancelled) return;
      const inputSection = document.querySelector('[data-tour="input-section"]');
      const progressBar = document.querySelector('[data-tour="progress-bar"]');
      const sessionIndicator = document.querySelector('[data-tour="session-indicator"]');
      const hamburgerMenu = document.querySelector('[data-tour="hamburger-menu"]');
      const feedbackButton = document.querySelector('[data-tour="feedback-button"]');

      if (inputSection && progressBar && sessionIndicator && hamburgerMenu && feedbackButton) {
        console.log('[Tour] All tour elements found in DOM, starting tour');
        tourAutoHandledRef.current = true;
        tourFinishHandledRef.current = false;
        setRunTour(true);
      } else {
        console.log('[Tour] Some elements missing (retry', retryCount + 1, '/', maxRetries, '):', {
          inputSection: !!inputSection,
          progressBar: !!progressBar,
          sessionIndicator: !!sessionIndicator,
          hamburgerMenu: !!hamburgerMenu,
          feedbackButton: !!feedbackButton
        });

        // Retry if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(checkElementsAndStartTour, 1000);
        } else {
          console.warn('[Tour] Failed to find all elements after', maxRetries, 'retries. Tour will not start automatically.');
        }
      }
    };

    // Initial check after 1 second
    const initialTimer = setTimeout(checkElementsAndStartTour, 1000);

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
    };
  }, [user, shouldShowOnboarding, startupDelay, currentView, chatLoading, conversationId, hasPendingPlan, messages.length, showNotificationBanner, isInstallable]);

  // Function to check if all todos are complete
  const checkTodosComplete = async () => {
    if (!conversationId) return;

    // Don't show modal if we just switched from todos view
    // (user already saw the modal there)
    if (justSwitchedFromTodos) {
      setJustSwitchedFromTodos(false);
      return;
    }

    // Don't show modal if user has already seen it for this completion
    // (prevents re-showing after they dismiss or click "Set New Goal")
    if (hasSeenCompletionModal) {
      return;
    }

    // Don't show modal if there's a pending plan (user is reviewing a new plan)
    const hasPendingPlan = messages.length > 0 &&
      messages[messages.length - 1].content?.includes('<TodoPlan:');
    if (hasPendingPlan) {
      // console.log('[App] Pending plan detected, skipping celebration modal');
      return;
    }

    try {
      const todos = await todosAPI.getTodos(conversationId);

      // Flatten all todos from all stages
      const allTodos = todos.flatMap(stage => stage.todos || []);

      // Get all non-deleted required todos
      const requiredTodos = allTodos.filter(todo => !todo.is_optional && !todo.is_deleted);

      // Check if all required todos are completed
      const allComplete = requiredTodos.length > 0 && requiredTodos.every(todo => todo.is_completed);

      if (allComplete && currentView === VIEWS.CHAT) {
        // console.log('[App] All todos complete! Showing celebration modal');
        setShowCompletionModal(true);
        setHasSeenCompletionModal(true);

        // Reset the flag after 5 minutes so modal can appear again
        setTimeout(() => {
          // console.log('[App] 5 minutes passed, allowing completion modal to show again');
          setHasSeenCompletionModal(false);
        }, 5 * 60 * 1000);
      } else if (!allComplete && hasSeenCompletionModal) {
        // If todos are no longer all complete (new plan approved), reset the flag
        // console.log('[App] Todos not all complete - resetting completion modal flag');
        setHasSeenCompletionModal(false);
      }
    } catch (err) {
      console.error('[App] Failed to check todos completion:', err);
    }
  };

  // Queued fact checking now handled by ChatArea component (inline display)

  // Check if session is joinable - use server-provided isJoinable flag
  useEffect(() => {
    if (!upcomingSession) {
      setCanJoinSession(false);
      return;
    }

    // Use server-calculated isJoinable flag
    setCanJoinSession(upcomingSession.isJoinable || false);
  }, [upcomingSession]);

  // Coach slot in the header: with a session booked, open its details screen
  // (when, meeting link, join / reschedule / cancel); otherwise open the
  // in-app booking flow (coach picker → available slots).
  const handleSessionIndicatorClick = () => {
    if (upcomingSession) {
      navigateTo(VIEWS.COACHING_SESSION);
      return;
    }
    navigateTo(VIEWS.COACHING_SCHEDULE);
  };

  // Handler for rescheduling a session (called from the session details
  // screen: cancels the booking, then reopens the same coach's slots)
  const handleReschedule = async () => {
    if (!upcomingSession) return;

    try {
      // Cancel the current session
      await sessionsAPI.cancelSession(upcomingSession.id);

      // Replace the (now stale) session details screen with the scheduling
      // screen for the same coach, so Back can't land on a cancelled session.
      setSelectedCoachForScheduling(upcomingSession.coach);
      setUpcomingSession(null);
      replaceTo(VIEWS.COACHING_SCHEDULE, { coach: upcomingSession.coach });
    } catch (err) {
      console.error('[App] Failed to reschedule session:', err);
      alert('Failed to reschedule session. Please try again.');
    }
  };

  // Handler for canceling a session. Returns true when actually cancelled so
  // the session details screen knows to navigate away.
  const handleCancel = async () => {
    if (!upcomingSession) return false;

    const confirmed = window.confirm('Are you sure you want to cancel this coaching session?');
    if (!confirmed) return false;

    try {
      await sessionsAPI.cancelSession(upcomingSession.id);
      setUpcomingSession(null);
      // Inform the chat
      sendMessage('I just cancelled my upcoming coaching session.');
      return true;
    } catch (err) {
      console.error('[App] Failed to cancel session:', err);
      alert('Failed to cancel session. Please try again.');
      return false;
    }
  };

  const handleJoyrideCallback = (data) => {
    const { status, type, index, action, lifecycle } = data;

    console.log('[Tour] Joyride callback:', {
      status,
      type,
      index,
      action,
      lifecycle,
      step: tourSteps[index]?.target
    });

    // Handle errors when target element doesn't exist
    if (status === 'error' || type === 'error:target_not_found') {
      console.warn('[Tour] Target not found, skipping step:', tourSteps[index]?.target);
      // Tour will automatically skip to next step
      return;
    }

    const finishedStatuses = [JOYRIDE_STATUS_FINISHED, JOYRIDE_STATUS_SKIPPED];
    // The tooltip's × (close) button fires action:'close' with status:'running'
    // — NOT a finished/skipped status. Without treating close as a terminal
    // dismissal, a user who closes the tour via × never has sawTutorial
    // persisted, so it auto-starts again on every load (the symptom that made
    // saw_tutorial stay null in the DB). Treat close as an end-of-tour.
    const dismissedViaClose = action === JOYRIDE_ACTION_CLOSE;

    if (finishedStatuses.includes(status) || dismissedViaClose) {
      // Tour ended (completed, skipped, or closed). Joyride fires this callback
      // many times in quick succession — guard with a ref so we only react once
      // per tour run (state updates are batched and would all see stale
      // runTour=true otherwise).
      if (tourFinishHandledRef.current) return;
      tourFinishHandledRef.current = true;

      console.log('[Tour] Tour ended with status:', status, 'action:', action);
      tourAutoHandledRef.current = true;
      setRunTour(false);
      setIsMenuOpen(false);
      if (user && !user.sawTutorial) {
        updateUser({ sawTutorial: true }).catch((err) => {
          console.warn('[Tour] Failed to persist sawTutorial:', err);
        });
      }
    }
  };

  // Drive the Mixpanel consent gate. This is the ONLY thing that initializes and
  // opts the SDK in/out — until analyticsConsent is true, mixpanelService stays
  // un-initialized and every tracking call is a no-op (nothing sent or persisted).
  useEffect(() => {
    if (analyticsConsent === null) return; // still loading — leave SDK disabled
    mixpanelService.setAnalyticsConsent(analyticsConsent === true);
  }, [analyticsConsent]);

  // Track App Opened and identify user in Mixpanel (only once per session).
  // Requires analytics consent — not just required-consent — before any tracking.
  useEffect(() => {
    const identifyUserAndTrack = async () => {
      if (user && !startupDelay && !hasTrackedAppOpened.current && !consentLoading && needsConsent === false && analyticsConsent === true) {
        try {
          // Fetch user stats for enhanced profile
          const userStats = await contentAPI.getStats();

          // Build complete user properties with stats
          const userProperties = mixpanelService.buildUserProperties(
            {
              email: user.email,
              name: user.name,
              thinkific_id: user.thinkificId,
              financial_experience: user.financialExperience,
              money_stage: user.moneyStage,
              age_range: user.ageRange,
              countries: user.countries,
              monthly_savings: user.monthlySavings,
              currentMission: user.currentMission,
              createdAt: user.createdAt,
            },
            {
              days_active: userStats.daysActive,
              total_sessions: userStats.totalSessions,
              journey_start_date: userStats.journeyStartDate,
              last_active_date: userStats.lastActiveDate,
              engagement_trend: userStats.engagementTrend,
              todos_completed: userStats.todosCompleted,
              articles_read: userStats.articlesRead,
              videos_watched: userStats.videosWatched,
            }
          );

          // Identify user in Mixpanel with full profile including stats
          mixpanelService.identify(user.email, userProperties);

          // Track App Opened (only once per session)
          mixpanelService.trackEvent('App Opened', {
            has_mission: !!user.currentMission,
            financial_experience: user.financialExperience,
            money_stage: user.moneyStage,
            journey_stage: userProperties.journey_stage,
            days_active: userStats.daysActive,
          });

          hasTrackedAppOpened.current = true;
        } catch (error) {
          console.error('[App] Failed to fetch user stats for Mixpanel:', error);

          // Fall back to basic identification without stats
          mixpanelService.identify(user.email, {
            name: user.name || user.email,
            email: user.email,
            signup_method: 'thinkific_sso',
            thinkific_id: user.thinkificId,
            financial_experience: user.financialExperience,
            money_stage: user.moneyStage,
            age_range: user.ageRange,
            countries: user.countries,
            monthly_savings: user.monthlySavings,
          });

          mixpanelService.trackEvent('App Opened', {
            has_mission: !!user.currentMission,
            financial_experience: user.financialExperience,
            money_stage: user.moneyStage,
          });

          hasTrackedAppOpened.current = true;
        }
      }
    };

    identifyUserAndTrack();
  }, [user, startupDelay, consentLoading, needsConsent, analyticsConsent]);

  // Track Screen Viewed when view changes (only when view actually changes)
  useEffect(() => {
    if (user && !startupDelay && currentView) {
      const screenNameMap = {
        [VIEWS.CHAT]: 'Chat',
        [VIEWS.TODOS]: 'Todo List',
        [VIEWS.ARTICLE_DETAIL]: 'Article Detail',
        [VIEWS.NEWS_DETAIL]: 'News Detail',
        [VIEWS.ALL_RESOURCES]: 'All Resources',
        [VIEWS.ALL_NEWS]: 'All News',
        [VIEWS.PROGRESS_DASHBOARD]: 'Progress Dashboard',
        [VIEWS.CALENDLY]: 'Book Coach',
        [VIEWS.COACHING_SCHEDULE]: 'Coaching Schedule',
        [VIEWS.COACH_DASHBOARD]: 'Coach Dashboard',
        [VIEWS.ORG_DASHBOARD]: 'Organization Dashboard',
        [VIEWS.DEV_DASHBOARD]: 'Dev Dashboard',
        [VIEWS.COMMUNITY_HUB]: 'Community Manager Hub',
      };

      const screenName = screenNameMap[currentView] || 'Unknown';

      // Only track if the view has actually changed
      if (lastTrackedView.current !== screenName) {
        mixpanelService.trackEvent('Screen Viewed', {
          screen_name: screenName,
          previous_screen: lastTrackedView.current || 'none',
        });
        lastTrackedView.current = screenName;
      }
    }
  }, [user, startupDelay, currentView]);

  // Load history when not showing onboarding
  useEffect(() => {
    if (user && !startupDelay && !shouldShowOnboarding) {
      // Check if there's a pending plan (change-objective scenario)
      const hasPendingPlan = messages.length > 0 && messages[messages.length - 1]?.content?.includes('<TodoPlan:');

      if (hasPendingPlan) {
        console.log('[App] Pending plan detected - starting change-objective flow');
        startOnboarding('change-objective');
      } else if (messages.length === 0) {
        // No messages yet, load history
        loadHistory();
      }
    }
  }, [user, startupDelay, shouldShowOnboarding, messages, loadHistory, startOnboarding]);

  // DISABLED: Action plan validation that was forcing users into onboarding
  // When users complete their action plan, they should stay in chat, not be forced into onboarding
  // If they want a new plan, they can request it via "Set New Goal" button or by asking Sophia
  /*
  useEffect(() => {
    const validateActionPlan = async () => {
      if (!user || shouldShowOnboarding || !conversationId || chatLoading) {
        return;
      }

      const hasPendingPlan = messages.length > 0 && messages[messages.length - 1]?.content?.includes('<TodoPlan:');
      if (hasPendingPlan) {
        return;
      }

      try {
        const todos = await todosAPI.getTodos(conversationId);
        const allTodos = todos.flatMap(stage => stage.todos || []);
        const activeTodos = allTodos.filter(todo => !todo.is_deleted && !todo.mission_changed);

        if (activeTodos.length === 0 && user.currentMission) {
          await updateUser({ currentMission: '' });
        }
      } catch (err) {
        console.error('[App] Failed to validate action plan:', err);
      }
    };

    validateActionPlan();
  }, [user, shouldShowOnboarding, conversationId, messages, updateUser, chatLoading]);
  */

  // Check if todos are complete when messages are loaded (app open or after sending)
  // ONLY in chat view - TodoList handles its own completion modal
  useEffect(() => {
    if (conversationId && messages.length > 0 && !chatLoading && currentView === VIEWS.CHAT) {
      // Small delay to ensure todos are synced
      const timer = setTimeout(() => {
        checkTodosComplete();
      }, 500);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, messages.length, chatLoading, currentView]);

  // Handle /landing route - always show landing page regardless of auth status
  // This must be checked BEFORE startupDelay to avoid flickering
  if (location.pathname === '/landing') {
    const handleShowLogin = () => {
      // Navigate to home route which will show login or main app
      navigate('/');
    };

    return (
      <Suspense fallback={<ScreenFallback />}>
        <LandingPage onShowLogin={handleShowLogin} />
      </Suspense>
    );
  }

  // Handle public /privacy and /tos routes - legal pages visible without auth.
  // Checked BEFORE startupDelay/auth so they render immediately (e.g. when
  // linked from an external page or shared directly).
  if (location.pathname === '/privacy') {
    return (
      <Suspense fallback={<ScreenFallback />}>
        <PrivacyPolicy onClose={() => navigate('/')} />
      </Suspense>
    );
  }
  if (location.pathname === '/tos') {
    return (
      <Suspense fallback={<ScreenFallback />}>
        <TermsOfService onClose={() => navigate('/')} />
      </Suspense>
    );
  }

  // Public, invite-only Meet Sophia pre-signup micro-session. Checked BEFORE
  // the org-slug catch-all ('meet-sophia' is also in RESERVED_PATHS) and
  // before startupDelay/auth so it renders immediately.
  if (location.pathname === '/meet-sophia') {
    return (
      <Suspense fallback={<ScreenFallback />}>
        <MeetSophia />
      </Suspense>
    );
  }

  // Public workshop content pages: /workshop/{slug} — curated articles/videos/
  // quizzes for live-workshop audiences, email-gated into meet-sophia.
  // Checked BEFORE startupDelay/auth so they render immediately.
  const workshopMatch = location.pathname.match(/^\/workshop\/([A-Za-z0-9][A-Za-z0-9._~-]*)\/?$/);
  if (workshopMatch) {
    return (
      <Suspense fallback={<ScreenFallback />}>
        <Workshop slug={workshopMatch[1].toLowerCase()} />
      </Suspense>
    );
  }

  // Handle public per-organization landing pages: /:slug
  // (a single path segment that isn't one of the app's own routes).
  // Checked BEFORE startupDelay/auth so the org page renders immediately.
  const orgSlugMatch = location.pathname.match(/^\/([A-Za-z0-9][A-Za-z0-9_-]*)\/?$/);
  if (orgSlugMatch && !RESERVED_PATHS.includes(orgSlugMatch[1].toLowerCase())) {
    return (
      <Suspense fallback={<ScreenFallback />}>
        <OrganizationLanding slug={orgSlugMatch[1]} />
      </Suspense>
    );
  }

  // Show startup loading screen
  if (startupDelay) {
    return <BrandLoadingScreen />;
  }

  // Only show loading screen if we're verifying an existing token (not during login)
  if (authLoading && !user) {
    // Check if we have a token - if so, we're verifying it
    const hasToken = localStorage.getItem('authToken');
    if (hasToken) {
      return <BrandLoadingScreen />;
    }
  }

  if (!user) {
    // Local auth (any host) or dev: show the email/password login form —
    // it also handles register / forgot / reset-password views in local mode.
    // (The subscription gate for a logged-in user runs AFTER compliance and
    // BEFORE onboarding — see the hasRestrictedAccess block below the
    // consent gate.)
    if (IS_LOCAL_AUTH || isDevHost()) {
      return (
        <div className={styles.app}>
          <Suspense fallback={<ScreenFallback />}>
            <LoginForm
              onLogin={login}
              onRegister={register}
              onResetPassword={resetPassword}
              isLoading={authLoading}
            />
          </Suspense>
        </div>
      );
    }

    // Production: the marketing landing page is deprecated. The effect above is
    // redirecting this unauthenticated visitor — to sophiawomen.com (no session),
    // or into Thinkific OAuth (/login & deep links). Render a brief loading
    // screen while that navigation happens.
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingText}>Sophia</div>
        <div className={styles.loadingSubtext}>Redirecting...</div>
      </div>
    );
  }

  // COMPLIANCE COMES FIRST: while consent status is unknown for a logged-in
  // user (initial check, or the re-check after an in-session register/login),
  // hold on a quiet frame — never flash the paywall or the app before it.
  if (user && consentLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingText}>Sophia</div>
      </div>
    );
  }

  // Show consent modal if user hasn't consented yet (BLOCKING)
  // Use !== false instead of === true to show modal even if consent check failed (needsConsent is null)
  if (user && !consentLoading && needsConsent !== false) {
    const handleConsent = async (consentData) => {
      try {
        await recordConsent(consentData);
        // Consent recorded successfully - user can now access the app
        console.log('[App] Consent recorded successfully');
      } catch (error) {
        console.error('[App] Failed to record consent:', error);
        throw error; // Let FirstTimeConsent component handle error display
      }
    };

    const handleDecline = () => {
      // User declined required consent - redirect back to Thinkific
      const thinkificUrl = `https://www.sophiawomen.com`;
      window.location.href = thinkificUrl;
    };

    return (
      <Suspense fallback={<ScreenFallback />}>
        <FirstTimeConsent
          onConsent={handleConsent}
          onDecline={handleDecline}
          orgName={user?.organizationName}
        />
      </Suspense>
    );
  }

  // DIRECTLY after compliance: the app-code gate. Runs before the
  // subscription gate (a code IS an access grant, so ask for it before
  // showing any paywall) and before onboarding — onboarding sets countries,
  // which doubles as the grandfather marker and would make this unreachable.
  // IMPORTANT: gate on !authLoading — `user` is hydrated from a stale cache on first
  // render, so checking hasRedeemedCode before /me resolves would flash this prompt
  // even when ASKCODES is disabled (server returns hasRedeemedCode=true in that case).
  if (user && !authLoading && !consentLoading && !user.hasRedeemedCode) {
    const handleCodeAccepted = () => {
      console.log('[App] App code accepted - reloading to refresh user data');
      // Reload page to refresh user data from server with updated hasRedeemedCode flag
      window.location.reload();
    };

    return (
      <Suspense fallback={<ScreenFallback />}>
        <AppCodeInput onCodeAccepted={handleCodeAccepted} />
      </Suspense>
    );
  }

  // Hold plan-less users on a splash while the Meet Sophia adoption check
  // runs (one quick call; reloads if a pre-signup plan was adopted). Runs
  // BEFORE the subscription gate so a Meet Sophia convert reaches the paywall
  // with her plan already adopted (correct "your plan is ready" copy, and no
  // reload underneath the paywall), and before onboarding so it never asks
  // for a goal she already gave Sophia.
  if (user && user.hasActivePlan === false && meetSophiaAdoptPending) {
    return <BrandLoadingScreen subtext="Getting things ready…" />;
  }

  // Then the subscription gate — right after consent (and the app code),
  // BEFORE onboarding. Flow: register → consent → Stripe → onboarding.
  // The server decides who pays: a user with no coverage (no org, a
  // 'self_paid' referral org, no family seat, no paid plan) is restricted and
  // is sent straight into checkout here (AccessRestricted redirects a new
  // signup to Stripe Checkout with no pitch page in between); a member of an
  // org that pays for her is not restricted and goes straight to onboarding.
  // Paying reloads with the new entitlement, and onboarding starts on that
  // fresh load.
  if (user && !authLoading && user.hasRestrictedAccess) {
    return (
      <div className={styles.app}>
        <Suspense fallback={<ScreenFallback />}>
          <AccessRestricted
            offerSelfRegister={user.accessOfferSelfRegister}
            reason={user.accessReason}
            hasOrg={user.accessHasOrg}
          />
        </Suspense>
        {/* A just-paid self-register lands here until the webhook flips
            access — the overlay polls and reloads once the plan is active. */}
        <CheckoutReturn />
      </div>
    );
  }

  // Show onboarding if needed
  if (user && shouldShowOnboarding) {
    return (
      <>
        {/* Return from Stripe Checkout (?checkout=success). Payment comes
            BEFORE onboarding, so when the webhook has already flipped access
            by the time Stripe sends her back, this is the screen she lands
            on — the celebration must still play here (renders nothing
            otherwise). */}
        <CheckoutReturn />
        <Suspense fallback={<BrandLoadingScreen />}>
        <OnboardingScreen
          user={user}
          flowItems={flowItems}
          flowType={flowType}
          onComplete={async (approvedConversationId) => {
            console.log('[App] Onboarding completed - refreshing data');
            console.log('[App] Current conversationId BEFORE refresh:', conversationId);
            completeOnboarding();
            // Hide ProgressBar immediately to prevent showing stale data
            setHideProgressBar(true);
            // CRITICAL: Invalidate all caches to fetch fresh data from new conversation
            stateManager.invalidate('messages');
            stateManager.invalidate('todos');
            stateManager.invalidate('progressData');
            stateManager.invalidate('conversationId');
            stateManager.invalidate('currentMission');
            // Drop the greeting from the conversation onboarding was using —
            // approval replaced it with a new one. Without this, that old
            // greeting sits in the chat while the real welcome message is
            // still being written.
            resetChat();
            // Wait for loadHistory to fetch new conversation data
            let newConversationId = await loadHistory();
            // The approved conversation can take a moment to become the
            // active one server-side; until it does, /history answers with the
            // old one. Re-ask (clearing state each time, so nothing stale is
            // ever on screen) rather than showing the wrong conversation.
            for (let attempt = 0; attempt < 5
              && approvedConversationId
              && newConversationId
              && String(newConversationId) !== String(approvedConversationId); attempt++) {
              console.log('[App] /history returned', newConversationId, '— waiting for approved conversation', approvedConversationId);
              await new Promise(resolve => setTimeout(resolve, 800));
              stateManager.invalidate('messages');
              stateManager.invalidate('conversationId');
              resetChat();
              newConversationId = await loadHistory();
            }
            console.log('[App] New conversationId from loadHistory:', newConversationId);
            console.log('[App] Current conversationId in state (may not be updated yet):', conversationId);
            // Wait for React state updates to propagate
            await new Promise(resolve => setTimeout(resolve, 300));
            // THEN force ProgressBar to remount with the new conversationId
            setProgressBarKey(prev => prev + 1);
            // Show ProgressBar again with fresh data
            setHideProgressBar(false);
            console.log('[App] ProgressBar refresh complete - should now show new data');
          }}
          onDismiss={() => {
            console.log('[App] Onboarding dismissed - user canceled change-objective flow');
            completeOnboarding();
            // User canceled - don't change their objective, just go back to previous view
            // (This could be chat or todos depending on where they started the flow)
          }}
          onUpdateUser={updateUser}
        />
        </Suspense>
        {/* Feedback Modal */}
        {isFeedbackOpen && (
          <Suspense fallback={null}>
            <FeedbackModal
              isOpen={isFeedbackOpen}
              onClose={() => setIsFeedbackOpen(false)}
            />
          </Suspense>
        )}
        {/* Floating Feedback Button - Also available during onboarding */}
        <FeedbackButton onClick={() => setIsFeedbackOpen(true)} />
      </>
    );
  }

  // console.log('[App] Rendering main app');

  // Handle returning from content (send reading time to LLM)
  const handleBackFromContent = () => {
    if (readingSession) {
      const { contentType, title, startTime } = readingSession;
      const durationMs = Date.now() - startTime;
      const durationSeconds = Math.floor(durationMs / 1000);

      // Only send message if they spent at least 3 seconds (avoid accidental clicks)
      if (durationSeconds >= 3) {
        // Categorize engagement level based on duration
        let engagementDescription;

        if (durationSeconds < 20) {
          engagementDescription = 'closed quickly without reading. Don\'t comment on or summarize the content. Simply acknowledge and ask what they\'d like to explore instead';
        } else if (durationSeconds <= 35) {
          engagementDescription = 'skimmed briefly. You can ask if they found it useful or if they have questions';
        } else {
          engagementDescription = 'spent time reading. You can ask what they thought or if anything stood out';
        }

        const message = `[User just finished reading "${title}" (${contentType}) for ${durationSeconds} seconds - ${engagementDescription}]`;
        sendMessage(message);
      }

      setReadingSession(null);
    }
    goBack();
  };

  // Handle returning from screens (Action Plan, Progress Dashboard)
  const handleBackFromScreen = async () => {
    if (visitingScreen) {
      const { screenType, startTime } = visitingScreen;
      const durationMs = Date.now() - startTime;
      const durationSeconds = Math.floor(durationMs / 1000);

      // Only send context if they spent at least 3 seconds
      if (durationSeconds >= 3) {
        if (screenType === 'action-plan') {
          // Fetch current todos to report on status
          try {
            const response = await fetch(`/api/todos?conversationId=${conversationId}`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            const tiers = await response.json();

            // A failed request returns an error OBJECT, not an array, and the
            // unguarded flatMap below threw TypeError. Guard rather than return:
            // this block must NOT exit the function, or it would skip the
            // setVisitingScreen(null) + goBack() at the end and strand the user
            // on the screen. This context is opportunistic — losing it is fine.
            if (Array.isArray(tiers)) {
              // Flatten all todos from all tiers
              const allTodos = tiers.flatMap(tier => tier.todos || []);

              // Calculate summary
              const completedCount = allTodos.filter(t => t.is_completed && !t.is_optional).length;
              const totalRequired = allTodos.filter(t => !t.is_optional && !t.is_deleted).length;

              // Find current tier (lowest tier with incomplete required todos)
              let currentTier = 1;
              for (const tier of tiers) {
                const hasIncomplete = (tier.todos || []).some(t => !t.is_completed && !t.is_optional && !t.is_deleted);
                if (hasIncomplete) {
                  currentTier = tier.tier_number;
                  break;
                }
              }

              // Find next action (first incomplete required todo in current tier)
              const nextAction = allTodos.find(t => !t.is_completed && !t.is_optional && !t.is_deleted)?.todo_text;

              const message = `[User just reviewed their Action Plan. Status: ${completedCount}/${totalRequired} required tasks complete, currently on Stage ${currentTier}${nextAction ? `, next action: "${nextAction}"` : ''}]`;
              sendMessage(message);
            } else {
              console.warn('[App] Skipping action-plan context: unexpected /api/todos response');
            }
          } catch (error) {
            console.error('[App] Failed to fetch todos for context:', error);
          }
        } else if (screenType === 'progress-dashboard') {
          // Fetch journey insights to report on last 2 observations
          try {
            const response = await fetch('/api/progress/journey-insights', {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            const insightsData = await response.json();

            const insights = insightsData.insights || [];
            const lastTwo = insights.slice(-2);

            if (lastTwo.length > 0) {
              const insightsSummary = lastTwo.map(i => `Stage ${i.stage}: "${i.insight}"`).join('; ');
              const message = `[User just reviewed their Progress Dashboard. Recent journey insights: ${insightsSummary}]`;
              sendMessage(message);
            } else {
              const message = `[User just reviewed their Progress Dashboard. ${insights.length === 0 ? 'No journey insights yet - they are just getting started.' : ''}]`;
              sendMessage(message);
            }
          } catch (error) {
            console.error('[App] Failed to fetch journey insights for context:', error);
          }
        }
      }

      setVisitingScreen(null);
    }
    goBack();
  };

  // Render view based on currentView
  const renderView = () => {
    // Sophia Together off → its screens resolve to the chat view instead of a
    // blank one (covers a flag flipped mid-session with a circles view on the
    // stack, and any URL/back-stack path that skipped the entry-point guards).
    // Same treatment for the coaching screens when ENABLE_COACHES is off.
    const view = (!circlesEnabled && SOPHIA_TOGETHER_VIEWS.includes(currentView)) ||
      (!coachesEnabled && COACHING_VIEWS.includes(currentView))
      ? VIEWS.CHAT
      : currentView;

    switch (view) {
      case VIEWS.TODOS:
        return (
          <TodoList
            conversationId={conversationId}
            objective={currentMission}
            onBackToChat={() => {
              handleBackFromScreen();
              setJustSwitchedFromTodos(true);
            }}
            onChangePlan={() => {
              // Don't navigate - just start onboarding directly
              // The onboarding screen will replace the current view
              startOnboarding('change-objective');
            }}
          />
        );

      case VIEWS.PROGRESS_DASHBOARD:
        return (
          <ProgressDashboard
            onBack={handleBackFromScreen}
            onNavigateToTasks={() => navigateTo(VIEWS.TODOS)}
          />
        );

      case VIEWS.BRACELET:
        return (
          <Bracelet
            onBack={handleBackFromScreen}
            onNavigateToTasks={() => navigateTo(VIEWS.TODOS)}
            animateBeadSlug={viewData?.animateBeadSlug}
          />
        );

      case VIEWS.MONEY_DATES:
        return (
          <MoneyDatesScreen onBack={handleBackFromScreen} />
        );

      // The three Sophia Together screens. When the feature is off they are
      // unreachable by design (no nav entries, deep links ignored) AND rewritten
      // to the chat view above, so a stale view stack can't strand the user.
      case VIEWS.CIRCLES:
        return (
          <CirclesScreen
            onBack={handleBackFromScreen}
            inviteToken={viewData?.inviteToken}
          />
        );

      case VIEWS.CIRCLE_SETTINGS:
        return (
          <CircleSettingsScreen
            circleId={activeCircleId}
            familyEnabled={familyEnabled}
            onBack={goBack}
            onLeft={() => { refreshCirclesList(); exitToPersonal(); goHome(); }}
            onChanged={refreshActiveCircle}
          />
        );

      case VIEWS.FAMILY:
        return (
          <FamilyPlanScreen onBack={handleBackFromScreen} />
        );

      case VIEWS.ARTICLE_DETAIL:
        return (
          <ArticleDetailView
            slug={viewData.slug}
            onBack={handleBackFromContent}
          />
        );

      case VIEWS.QUIZ_DETAIL:
        return (
          <Quiz
            slug={viewData.slug}
            variant="fullscreen"
            onComplete={(result) => {
              // Notify the coach of the quiz results (hidden context message),
              // mirroring the in-chat quiz card behavior.
              if (result && result.sectionResults) {
                const resultsSummary = result.sectionResults
                  .map(r => `${r.sectionTitle}: ${r.resultTitle}${r.score !== undefined ? ` (${r.score}/${r.maxScore})` : ''}`)
                  .join(', ');
                const message = `[User just completed the "${result.quizTitle}" quiz. Results: ${resultsSummary}]`;
                sendMessage(message, null, { isHidden: true });
              }
            }}
            onClose={() => goHome()}
          />
        );

      case VIEWS.VIDEO_DETAIL:
        return (
          <DeepLinkVideo
            videoId={viewData.videoId}
            onClose={() => goHome()}
          />
        );

      case VIEWS.NEWS_DETAIL:
        return (
          <NewsDetailView
            newsId={viewData.newsId}
            onBack={handleBackFromContent}
          />
        );

      case VIEWS.ALL_ARTICLES:
        return (
          <AllLearningResourcesView
            onBack={goBack}
            onArticleClick={(slug, metadata) => {
              // No tracking from library - only track content from chat recommendations
              navigateTo(VIEWS.ARTICLE_DETAIL, { slug });
            }}
            onVideoClick={(video) => {
              // Open video in new tab - no tracking (opens externally)
              const targetUrl = video.lessonUrl || video.videoUrl;
              if (targetUrl) {
                window.open(targetUrl, '_blank', 'noopener,noreferrer');
              }
            }}
          />
        );

      case VIEWS.ALL_NEWS:
        return (
          <AllNewsView
            onBack={goBack}
            onNewsClick={(newsId, metadata) => {
              // No tracking from library - only track content from chat recommendations
              navigateTo(VIEWS.NEWS_DETAIL, { newsId });
            }}
          />
        );

      case VIEWS.CALENDLY:
        // The content is a cross-origin iframe of the marketing site, which
        // renders its own Sophia header + hamburger that we cannot suppress
        // from here (UX-16). Our half of that finding is fixed: the app-side
        // bar is the shared PageHeader (it was a hand-rolled inline header
        // with an unlabeled back button and the app's only sans-serif title).
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: 'var(--color-surface)' }}>
            <PageHeader title="Book a Coaching Session" onBack={goBack} backLabel="Chat" />
            <iframe
              src="https://www.sophiawomen.com/coaching"
              style={{ flex: 1, border: 'none', width: '100%' }}
              title="Book a Coaching Session"
            />
          </div>
        );

      case VIEWS.COACHING_SCHEDULE:  // In-app booking: coach picker → slots (coach optional)
        // Keeps the standard app top bar with the Coach slot active; the
        // You / Circles slots navigate away like they do from the chat.
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(var(--vh, 1vh) * 100)', backgroundColor: 'var(--color-surface)' }}>
            <Header
              onOpenMenu={() => setIsMenuOpen(true)}
              navActive="coach"
              onNavYou={() => { exitToPersonal(); goHome(); }}
              onNavCircles={handleOpenCircles}
              onNavCoach={() => { if (upcomingSession) navigateTo(VIEWS.COACHING_SESSION); }}
              showCircles={circlesEnabled}
              showCoach={coachesEnabled}
              circlesUnread={myCircles.some(c => c.hasUnread)}
              circlesLocked={!canUseCircles}
              coachScheduled={!!upcomingSession}
              coachJoinable={canJoinSession}
            />
            <SchedulingScreen
              coach={selectedCoachForScheduling || viewData.coach}
              onBack={() => {
                setSelectedCoachForScheduling(null);
                goBack();
              }}
              onBookingComplete={(session) => {
                setUpcomingSession(session);
                setSelectedCoachForScheduling(null);
                // Send a message to the chat about the booking with coach name and formatted time
                const coachName = session.coach?.displayName || 'a coach';
                sendMessage(`I just scheduled a coaching session with ${coachName} for ${formatSessionTime(session.scheduled_start)}`);
                // Land on the session details screen (when, meeting link,
                // coach info) instead of bouncing straight back to the chat;
                // the finished booking step is replaced, so Back skips it.
                // The "Your session is booked" confirmation overlays it first.
                setJustBookedSession(session);
                replaceTo(VIEWS.COACHING_SESSION);
              }}
            />
          </div>
        );

      case VIEWS.COACHING_SESSION:  // Booked-session details: when, meeting link, coach info
        return (
          <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(var(--vh, 1vh) * 100)', backgroundColor: 'var(--color-surface)' }}>
            <Header
              onOpenMenu={() => setIsMenuOpen(true)}
              navActive="coach"
              onNavYou={() => { exitToPersonal(); goHome(); }}
              onNavCircles={handleOpenCircles}
              onNavCoach={refreshUpcomingSession}
              showCircles={circlesEnabled}
              showCoach={coachesEnabled}
              circlesUnread={myCircles.some(c => c.hasUnread)}
              circlesLocked={!canUseCircles}
              coachScheduled={!!upcomingSession}
              coachJoinable={canJoinSession}
            />
            <SessionDetailsScreen
              session={upcomingSession}
              onSessionOver={refreshUpcomingSession}
              onBack={goBack}
              onJoin={(session) => {
                // Open Zoom meeting in new tab
                if (session.zoom_join_url) {
                  window.open(session.zoom_join_url, '_blank', 'noopener,noreferrer');
                }
              }}
              onReschedule={handleReschedule}
              onCancel={async () => {
                const cancelled = await handleCancel();
                if (cancelled) goHome();
              }}
              onBookNew={() => navigateTo(VIEWS.COACHING_SCHEDULE)}
            />
          </div>
        );

      case VIEWS.COACH_DASHBOARD:
        return (
          <CoachDashboard
            onBack={goBack}
          />
        );

      case VIEWS.ORG_DASHBOARD:
        return (
          <OrganizationDashboard
            organizationId={user?.adminOfOrganizations?.[0]?.id}
            organizationName={user?.adminOfOrganizations?.[0]?.name}
            orgType={user?.adminOfOrganizations?.[0]?.orgType || user?.adminOfOrganizations?.[0]?.OrgType}
            onBack={goBack}
          />
        );

      case VIEWS.DEV_DASHBOARD:
        return (
          <DevDashboard
            onBack={goBack}
          />
        );

      case VIEWS.COMMUNITY_HUB:
        return (
          <CommunityHub
            onBack={goBack}
          />
        );

      case VIEWS.SETTINGS:
        return (
          <SettingsScreen
            user={user}
            onUpdateUser={updateUser}
            onLogout={logout}
            onClose={goBack}
          />
        );

      case VIEWS.PRIVACY_POLICY:
        return (
          <PrivacyPolicy
            onClose={goBack}
          />
        );

      case VIEWS.TERMS_OF_SERVICE:
        return (
          <TermsOfService
            onClose={goBack}
          />
        );

      case VIEWS.CHAT:
      default:
        return (
          <>
            {/* The COMPLETE top-bar stack (install banner, brand header,
                circle band / bracelet strip, session banner) slides up out of
                the way while the mobile keyboard is open, so the LLM's
                question stays readable above the input. */}
            <div className={`${styles.topBars} ${keyboardOpen ? styles.topBarsHidden : ''}`}>
            {/* One interruption at a time: the notification modal outranks the
                install prompt, which outranks the product tour. */}
            {isInstallable && !installBannerScrolledAway && !canJoinSession && !showNotificationBanner && (
              <Suspense fallback={null}>
                <PWAInstallPrompt
                  onInstall={promptInstall}
                  onDismiss={dismissPrompt}
                  onScrollAway={() => setInstallBannerScrolledAway(true)}
                  isIOS={isIOS}
                />
              </Suspense>
            )}
            <Header
              onOpenMenu={() => setIsMenuOpen(true)}
              navActive={activeCircleId ? 'circles' : 'you'}
              onNavYou={() => exitToPersonal()}
              onNavCircles={handleOpenCircles}
              onNavCoach={handleSessionIndicatorClick}
              showCircles={circlesEnabled}
              showCoach={coachesEnabled}
              circlesUnread={myCircles.some(c => c.hasUnread)}
              circlesLocked={!canUseCircles}
              coachScheduled={!!upcomingSession}
              coachJoinable={canJoinSession}
            />
            {activeCircleId ? (
              <Suspense fallback={null}>
                <CircleTopBand
                  circles={myCircles}
                  activeCircleId={activeCircleId}
                  activeCircle={activeCircle}
                  canCreate={canCreateCircle}
                  onSelectCircle={enterCircle}
                  onExitToPersonal={exitToPersonal}
                  onCreate={handleCreateCircle}
                  onOpenSettings={() => navigateTo(VIEWS.CIRCLE_SETTINGS)}
                />
              </Suspense>
            ) : (
              conversationId && !hasPendingPlan && !hideProgressBar && (
                <ProgressBar
                  key={progressBarKey}
                  conversationId={conversationId}
                  onNavigateToTodos={() => navigateTo(VIEWS.TODOS)}
                  onNavigateToDashboard={() => navigateTo(VIEWS.BRACELET)}
                  onTodoComplete={(response) => {
                    // Invalidate caches if stage was unlocked
                    if (response.stageUnlocked) {
                      stateManager.invalidate('messages');
                      stateManager.invalidate('todos');
                      stateManager.invalidate('progressData');
                      loadHistory();
                    }
                  }}
                  forceMinimized={
                    // On page load the bracelet is VISIBLE right away
                    // (product direction) — the reveal-gate below only kicks
                    // in once something has revealed this session, i.e. for
                    // replies after the user engages. From then on the
                    // bracelet hides while a reply is loading/animating, and
                    // ProgressBar itself decides whether to expand (only
                    // while the chat is scrolled fully down — scrolling up
                    // into history hides the bracelet).
                    chatLoading ||
                    (() => {
                      if (revealedMessageId === null) return false;
                      for (let i = messages.length - 1; i >= 0; i--) {
                        const m = messages[i];
                        if (m.role === 'assistant' && m.content?.trim()) {
                          return revealedMessageId !== m.id;
                        }
                      }
                      return true;
                    })()
                  }
                />
              )
            )}
            {/* Show join banner only when session is joinable (within ±10 minutes) */}
            {!activeCircleId && upcomingSession && canJoinSession && (
              <Suspense fallback={null}>
                <SessionBanner
                  session={upcomingSession}
                  onJoin={(session) => {
                    if (session.zoom_join_url) {
                      // Open Zoom meeting in new tab
                      window.open(session.zoom_join_url, '_blank', 'noopener,noreferrer');
                    } else {
                      // No meeting link on the session yet — open the details
                      // screen, which explains the link status, instead of a
                      // click that silently does nothing.
                      navigateTo(VIEWS.COACHING_SESSION);
                    }
                  }}
                  onDismiss={() => setUpcomingSession(null)}
                />
              </Suspense>
            )}
            </div>
            {activeCircleId ? (
              <Suspense fallback={null}>
                <CircleChatArea
                  messages={circleChat.messages}
                  sophiaThinking={circleChat.sophiaThinking}
                  loading={circleChat.loading}
                />
              </Suspense>
            ) : (
            <ChatArea
              messages={messages}
              isLoading={chatLoading}
              conversationId={conversationId}
              currentMission={currentMission}
              hasMoreMessages={hasMoreMessages}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadAllMessages}
              onPlanApprove={async () => {
                // After plan approval, sync React state with the new conversationId
                stateManager.invalidate('messages');
                stateManager.invalidate('todos');
                stateManager.invalidate('progressData');
                stateManager.invalidate('conversationId');
                stateManager.invalidate('currentMission');
                setHideProgressBar(true);
                const newId = await loadHistory();
                console.log('[App] Plan approved in chat - new conversationId:', newId);
                setProgressBarKey(prev => prev + 1);
                setHideProgressBar(false);
              }}
              onPlanEdit={(message) => {
                // User wants to edit the plan - send the message to continue conversation
                if (message) {
                  sendMessage(message);
                }
              }}
              onArticleClick={(slug, metadata) => {
                // Start tracking reading session
                setReadingSession({
                  contentType: 'article',
                  title: metadata?.title || 'an article',
                  startTime: Date.now()
                });
                navigateTo(VIEWS.ARTICLE_DETAIL, { slug });
              }}
              onNewsClick={(newsId, metadata) => {
                // Start tracking reading session
                setReadingSession({
                  contentType: 'news',
                  title: metadata?.headline || 'a news article',
                  startTime: Date.now()
                });
                navigateTo(VIEWS.NEWS_DETAIL, { newsId });
              }}
              onCoachClick={(coach) => {
                // Navigate to scheduling screen with selected coach
                setSelectedCoachForScheduling(coach);
                navigateTo(VIEWS.COACHING_SCHEDULE, { coach });
              }}
              onQuizComplete={(result) => {
                // User just completed a quiz - send results to LLM for personalized response
                // Use isHidden: true so this context message doesn't appear in chat history
                if (result && result.sectionResults) {
                  const resultsSummary = result.sectionResults
                    .map(r => `${r.sectionTitle}: ${r.resultTitle}${r.score !== undefined ? ` (${r.score}/${r.maxScore})` : ''}`)
                    .join(', ');
                  const message = `[User just completed the "${result.quizTitle}" quiz. Results: ${resultsSummary}]`;
                  sendMessage(message, null, { isHidden: true });
                }
              }}
              onMessageRevealComplete={handleMessageRevealComplete}
              scrollToBottomTrigger={scrollToBottomTrigger}
              liveMessageIds={liveMessageIds}
            />
            )}
            {!activeCircleId && (() => {
              // Show chips only after the current assistant message has fully
              // revealed (animation done). MessageBubble fires onRevealComplete
              // with its id; we match that against the latest assistant message.
              let lastAssistantId = null;
              let assistantCount = 0;
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === 'assistant' && messages[i].content?.trim()) {
                  if (lastAssistantId == null) lastAssistantId = messages[i].id;
                  assistantCount++;
                }
              }
              const chipsReady = lastAssistantId != null && revealedMessageId === lastAssistantId;
              // Suppress chips on the initial greeting — let the user form
              // their own first message before offering canned options.
              if (assistantCount <= 1) return null;
              if (hasPendingPlan || !chipsReady) return null;
              return (
                <SuggestionChips
                  suggestions={suggestions}
                  disabled={chatLoading}
                  onPick={(s) => {
                    setScrollToBottomTrigger(prev => prev + 1);
                    sendSuggestion(s);
                  }}
                />
              );
            })()}
            {activeCircleId ? (
              <InputSection
                onSendMessage={(msg) => { circleChat.send(msg).catch(() => {}); }}
                isLoading={circleChat.sending}
              />
            ) : (!hasPendingPlan && (
              // Stays mounted while Sophia thinks — the inline "(thinking...)"
              // bubble replaced the screen takeover, so hiding the input made
              // the layout jump. isLoading disables sending until the reply.
              <InputSection
                onSendMessage={(msg) => {
                  setScrollToBottomTrigger(prev => prev + 1);
                  sendMessage(msg);
                }}
                isLoading={chatLoading}
              />
            ))}
            {showNotificationBanner && (
              <Suspense fallback={null}>
                <NotificationPermissionModal
                  onAllow={() => {
                    console.log('[App] Allow button clicked');
                    requestNotificationPermission();
                  }}
                  onDismiss={() => {
                    console.log('[App] Dismiss button clicked');
                    dismissNotificationBanner();
                  }}
                />
              </Suspense>
            )}
            {showCompletionModal && !activeCircleId && (
              <Suspense fallback={null}>
                <TodoCompletionModal
                  isOpen
                  onClose={() => setShowCompletionModal(false)}
                  onSetNewGoal={() => {
                    setShowCompletionModal(false);
                    // Send message to LLM to find new objective
                    sendMessage("I want to find a new financial objective");
                  }}
                />
              </Suspense>
            )}
          </>
        );
    }
  };

  // Main render (only reached after onboarding is complete)
  return (
    <div className={activeCircleId ? `${styles.app} ${styles.circleMode}` : styles.app}>
      {/* Outer boundary for the code-split screens. Lazy pieces *inside* the
          chat view carry their own inner boundaries so a circle band or modal
          chunk never blanks the whole conversation. */}
      <Suspense fallback={<ScreenFallback />}>{renderView()}</Suspense>
      {/* Return from Stripe Checkout (?checkout=success) — renders nothing otherwise */}
      <CheckoutReturn />
      {/* Return from a coaching-credits Stripe Checkout — lands the user on
          the booking screen so they can spend what they just bought */}
      <CoachingCheckoutReturn onDone={() => navigateTo(VIEWS.COACHING_SCHEDULE)} />
      {/* Just booked a coaching session — same green stage; dismissing reveals
          the session details screen already underneath */}
      <BookingConfirmed session={justBookedSession} onDone={() => setJustBookedSession(null)} />
      {/* Global Bead Unlock Modal - shows beads one at a time from queue */}
      {currentBeadModal && (
        <Suspense fallback={null}>
        <BeadUnlockModal
          bead={currentBeadModal.bead}
          unlockStory={currentBeadModal.unlockStory}
          awardedReason={currentBeadModal.awardedReason}
          onDismiss={async () => {
            const dismissedSlug = currentBeadModal.bead?.bead_slug;
            // Mark bead as viewed in backend
            if (currentBeadModal.userBeadId) {
              try {
                await beadsAPI.markBeadViewed(currentBeadModal.userBeadId);
                // Notify Bracelet + ProgressBar mini-bracelet to refresh and
                // animate the new bead dropping into place.
                window.dispatchEvent(new CustomEvent('beadViewed', {
                  detail: { slug: dismissedSlug }
                }));
              } catch (err) {
                console.error('[App] Failed to mark bead as viewed:', err);
              }
            }
            // Clear current - useEffect will pick up next from queue
            setCurrentBeadModal(null);
          }}
        />
        </Suspense>
      )}
      {/* Analytics prompt - shows after 2 days if user hasn't opted in */}
      {user && !shouldShowOnboarding && (
        <Suspense fallback={null}><AnalyticsPrompt /></Suspense>
      )}
      {/* Hamburger menu should never show during onboarding */}
      {!shouldShowOnboarding && (
        <HamburgerMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onNavigateTasks={() => {
            setIsMenuOpen(false);
            setVisitingScreen({
              screenType: 'action-plan',
              startTime: Date.now(),
              data: null
            });
            navigateTo(VIEWS.TODOS);
          }}
          onNavigateProgress={() => {
            setIsMenuOpen(false);
            setVisitingScreen({
              screenType: 'progress-dashboard',
              startTime: Date.now(),
              data: null
            });
            navigateTo(VIEWS.PROGRESS_DASHBOARD);
          }}
          onNavigateBracelet={() => {
            setIsMenuOpen(false);
            setVisitingScreen({
              screenType: 'bracelet',
              startTime: Date.now(),
              data: null
            });
            navigateTo(VIEWS.BRACELET);
          }}
          onNavigateArticles={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.ALL_ARTICLES);
          }}
          onNavigateNews={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.ALL_NEWS);
          }}
          onNavigateMoneyDates={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.MONEY_DATES);
          }}
          // Undefined (not a no-op) when Sophia Together is switched off: the
          // menu drops any item whose handler is missing.
          onNavigateCircles={circlesEnabled ? () => {
            setIsMenuOpen(false);
            handleOpenCircles();
          } : undefined}
          onNavigateFamily={circlesEnabled ? () => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.FAMILY);
          } : undefined}
          // Undefined when human coaching is off — the menu drops the entry.
          onNavigateCoachDashboard={coachesEnabled ? () => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.COACH_DASHBOARD);
          } : undefined}
          onNavigateOrgDashboard={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.ORG_DASHBOARD);
          }}
          onNavigateDevDashboard={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.DEV_DASHBOARD);
          }}
          onNavigateCommunityHub={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.COMMUNITY_HUB);
          }}
          onFeedback={() => {
            setIsMenuOpen(false);
            setIsFeedbackOpen(true);
          }}
          onNavigateSettings={() => {
            setIsMenuOpen(false);
            navigateTo(VIEWS.SETTINGS);
          }}
          user={user}
        />
      )}
      {showCreateCircle && (
        <Suspense fallback={null}>
        <CreateCircleFlow
          familyEnabled={familyEnabled}
          onClose={() => setShowCreateCircle(false)}
          onNeedTogether={() => {
            setShowCreateCircle(false);
            navigateTo(VIEWS.FAMILY);
          }}
          onCreated={(circleId) => {
            setShowCreateCircle(false);
            refreshCirclesList();
            enterCircle(circleId);
            goHome();
          }}
        />
        </Suspense>
      )}
      {/* Sophia Together upsell — shown when a locked Circles slot or the locked
          "+" is tapped. On success the user becomes a family account (can create
          circles), so we resume whatever they were trying to do. */}
      {circleUpsell && (
        <Suspense fallback={null}>
        <MockCheckout
          plan="family"
          onClose={() => setCircleUpsell(null)}
          onSuccess={async () => {
            const intent = circleUpsell;
            setCircleUpsell(null);
            try { setFamilyStatus(await familyAPI.getFamily()); } catch (e) { /* best-effort */ }
            const list = await refreshCirclesList();
            if (intent === 'create') {
              setShowCreateCircle(true);
            } else if (list && list.length > 0) {
              enterCircle(list[0].id);
              goHome();
            } else {
              setShowCreateCircle(true);
            }
          }}
        />
        </Suspense>
      )}
      {/* Feedback Modal */}
      {isFeedbackOpen && (
        <Suspense fallback={null}>
          <FeedbackModal
            isOpen={isFeedbackOpen}
            onClose={() => setIsFeedbackOpen(false)}
          />
        </Suspense>
      )}
      {/* Floating Feedback Button */}
      <FeedbackButton onClick={() => setIsFeedbackOpen(true)} />
      {/* Queued fact display now handled inline by ChatArea component */}
      {/* Session details now render as a full screen (VIEWS.COACHING_SESSION) */}
      {/* Onboarding Tour — mounted on first run so react-joyride stays in its
          own chunk (see `tourMounted` above). */}
      {tourMounted && (
      <Suspense fallback={null}>
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        disableScrolling={false}
        spotlightClicks
        callback={handleJoyrideCallback}
        floaterProps={{
          disableAnimation: false,
          hideArrow: false,
        }}
        styles={{
          options: {
            zIndex: 250, // Above hamburger (200), below feedback modal (300)
            // NOTE: these four MUST stay literal hex — react-joyride runs
            // hexToRGB() on primaryColor to build the beacon halo, and passes
            // arrowColor into react-floater's arrow. A var() reference would
            // parse to an empty rgba() there. Values mirror --color-primary
            // and --color-surface exactly.
            primaryColor: '#1b4942', // = var(--color-primary)
            textColor: '#1b4942', // = var(--color-primary)
            backgroundColor: '#ffffff', // = var(--color-surface)
            arrowColor: '#ffffff', // = var(--color-surface)
          },
          tooltip: {
            borderRadius: 12,
            fontSize: 15,
          },
          tooltipTitle: {
            fontFamily: 'Caudex, serif',
            fontSize: 20,
            fontWeight: 600,
            color: 'var(--color-primary)',
          },
          tooltipContent: {
            padding: '12px 0',
            lineHeight: 1.5,
            color: 'var(--color-ink-soft)',
          },
          buttonNext: {
            backgroundColor: 'var(--color-primary)',
            borderRadius: 8,
            padding: '8px 16px',
          },
          // Back/Skip were --color-sage (#6e8e87) on the white tooltip: 3.57:1,
          // failing WCAG 1.4.3. --color-text-muted is the darkest-safe swap.
          buttonBack: {
            color: 'var(--color-text-muted)',
            marginRight: 8,
          },
          buttonSkip: {
            color: 'var(--color-text-muted)',
          },
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip',
        }}
      />
      </Suspense>
      )}
      {/* Update Notification - Shows when new app version is available */}
      <UpdateNotification />
    </div>
  );
}

export default App;
